import { ImageType } from "@/components/ui/tree/tree-context-menu/actions/upload-action";
import { GUID } from "@faro-lotv/foundation";
import { Vector3 } from "three";
import { create } from "zustand";

export type NewWaypoint = {
  /** The preliminary ID of the new waypoint to create. */
  id: GUID;

  /** The area where to create the waypoint in. */
  areaId: GUID;

  /** The type of image data uploaded with the waypoint. */
  type: ImageType;

  /** The name of the waypoint to create. */
  name: string;

  /** The three.js world position of where to create the waypoint. */
  worldPosition: Vector3;

  /** The file containing image data for the waypoint. */
  files: File[];
};

export type NewWaypointsState = {
  /** The ID of the waypoint currently being edited. */
  waypointForNameEditingId: GUID | undefined;

  /** All new waypoints that are in the process of being added. */
  newWaypoints: NewWaypoint[];

  /** Applies a name edit to a waypoint and disables its editing state. */
  applyNameEditToWaypoint(waypointId: GUID, newName: string): void;

  /** Cancels the name editing of a waypoint. */
  cancelNameEdit(): void;

  /** Adds a new waypoint to be added. Immediately puts it into editing mode. */
  addNewWaypoint(newWaypoint: NewWaypoint): void;

  /** Removes a waypoint from the store. */
  removeNewWaypoint(waypointId: GUID): void;
};

/** The NewWaypointsStore contains the state of waypoints that are currently in the process of being added. */
export const useNewWaypointsStore = create<NewWaypointsState>()((set) => ({
  newWaypoints: [],
  waypointForNameEditingId: undefined,

  addNewWaypoint(newWaypoint: NewWaypoint) {
    set((state) => ({
      newWaypoints: [...state.newWaypoints, newWaypoint],
      waypointForNameEditingId: newWaypoint.id,
    }));
  },

  applyNameEditToWaypoint(waypointId: GUID, newName: string) {
    set((state) => ({
      newWaypoints: state.newWaypoints.map((waypoint) =>
        waypoint.id === waypointId
          ? {
              ...waypoint,
              name: newName,
            }
          : waypoint,
      ),
      // Reset the editing waypoint only, if the currently edited waypoint is the one we removed.
      waypointForNameEditingId:
        state.waypointForNameEditingId === waypointId
          ? undefined
          : state.waypointForNameEditingId,
    }));
  },

  cancelNameEdit() {
    set(() => ({
      waypointForNameEditingId: undefined,
    }));
  },

  removeNewWaypoint(waypointId: GUID) {
    set((state) => ({
      newWaypoints: state.newWaypoints.filter(
        (waypoint) => waypoint.id !== waypointId,
      ),
      waypointForNameEditing:
        state.waypointForNameEditingId === waypointId
          ? undefined
          : state.waypointForNameEditingId,
    }));
  },
}));
